import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  OrganisationTagExternal,
  useOrganisationTagUpdateSubscriptionStatusMutation,
} from '../../../../services';

interface SetTagNotifiedArgs {
  tagId: number;
  isNotified: boolean;
}

export const useTagNotification = () => {
  const { t } = useTranslation();

  const [updateSubscriptionStatus] = useOrganisationTagUpdateSubscriptionStatusMutation();

  const [notifiedById, setNotifiedById] = useState<Record<number, boolean>>({});

  const [isNotificationPending, setIsNotificationPending] = useState(false);

  const isTagNotified = useCallback(
    ({ id, subscriptionStatus }: OrganisationTagExternal) => notifiedById[id] ?? subscriptionStatus,
    [notifiedById]
  );

  const setTagNotified = useCallback(
    ({ tagId, isNotified }: SetTagNotifiedArgs) =>
      setNotifiedById((notifiedByIdPrev) => ({ ...notifiedByIdPrev, [tagId]: isNotified })),
    []
  );

  const toggleTagNotification = useCallback(
    async (tag: OrganisationTagExternal) => {
      const { id: tagId } = tag;

      const status = !isTagNotified(tag);

      setTagNotified({ tagId, isNotified: status });

      setIsNotificationPending(true);

      const revertTag = () => {
        setTagNotified({ tagId, isNotified: !status });
        toast.error(t('common.error-message'));
      };

      try {
        const isUpdated = await updateSubscriptionStatus({ tagId, status }).unwrap();

        if (!isUpdated) {
          revertTag();
        }
      } catch (_) {
        revertTag();
      } finally {
        setIsNotificationPending(false);
      }
    },
    [isTagNotified, setTagNotified, t, updateSubscriptionStatus]
  );

  return useMemo(
    () => ({ isTagNotified, setTagNotified, toggleTagNotification, isNotificationPending }),
    [isNotificationPending, isTagNotified, setTagNotified, toggleTagNotification]
  );
};
